import { useState } from 'react'

import { cn } from '#/shared/lib/utils'
import {
  CallInboundIcon,
  CallMissedIcon,
  CallOutboundIcon,
  UserOutlineIcon,
} from '#/shared/ui/icons'

import {
  AVATAR_TONE_CLASS,
  getInitials,
  pickAvatarTone,
} from '../lib/avatar-tone'
import type { ActivityIcon, AvatarTone } from '../model/types'

const ICON_GLYPH: Record<ActivityIcon, typeof UserOutlineIcon> = {
  user: UserOutlineIcon,
  'call-missed': CallMissedIcon,
  'call-outbound': CallOutboundIcon,
  'call-no-answer': CallOutboundIcon,
  'call-inbound': CallInboundIcon,
  'call-declined': CallMissedIcon,
}

/** Call-direction icons carry a fixed tone; `user` inherits the item's tone. */
const ICON_TONE: Partial<Record<ActivityIcon, AvatarTone>> = {
  'call-missed': 'red',
  'call-outbound': 'blue',
  'call-no-answer': 'gray',
  'call-inbound': 'green',
  'call-declined': 'gray',
}

interface ActivityAvatarProps {
  title: string
  tone?: AvatarTone
  icon?: ActivityIcon
  /** Customer photo; when absent or it fails to load, the initials/glyph show. */
  imageUrl?: string
}

export function ActivityAvatar({
  title,
  tone,
  icon,
  imageUrl,
}: ActivityAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false)

  if (imageUrl && !imgFailed) {
    return (
      <img
        src={imageUrl}
        alt={title}
        onError={() => setImgFailed(true)}
        className="size-8 shrink-0 rounded-full object-cover select-none"
      />
    )
  }

  const resolvedTone =
    (icon && ICON_TONE[icon]) ?? tone ?? pickAvatarTone(title)
  const Glyph = icon ? ICON_GLYPH[icon] : null

  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-medium select-none',
        AVATAR_TONE_CLASS[resolvedTone],
      )}
    >
      {Glyph ? <Glyph className="size-4" /> : getInitials(title)}
    </span>
  )
}
